import type { Locale, Translations } from '../types/types';
import { mapToSupportedLocale } from '../utils/mapToSupportedLocale';

export let translations: Translations;

export async function loadClientTranslations(localeString?: string) {
  const locale = mapToSupportedLocale(localeString);

  translations = await loadStringsAsync(locale);

  return;
}

// Webpack doesn't support dynamic strings in imports, so we pre-define all the imports here
const importMap: Record<Locale, () => Promise<{ default: Translations }>> = {
  en: () => import('../locales/en.json'),
  da: () => import('../locales/da.json'),
  de: () => import('../locales/de.json'),
  es: () => import('../locales/es.json'),
  fi: () => import('../locales/fi.json'),
  fr: () => import('../locales/fr.json'),
  it: () => import('../locales/it.json'),
  ko: () => import('../locales/ko.json'),
  nb: () => import('../locales/nb.json'),
  nl: () => import('../locales/nl.json'),
  pl: () => import('../locales/pl.json'),
  'pt-BR': () => import('../locales/pt-BR.json'),
  'pt-PT': () => import('../locales/pt-PT.json'),
  sv: () => import('../locales/sv.json'),
  th: () => import('../locales/th.json'),
  tr: () => import('../locales/tr.json'),
  cy: () => import('../locales/cy.json'),
  'is-IS': () => import('../locales/is-IS.json'),
  'zh-CN': () => import('../locales/zh-CN.json'),
  'zh-TW': () => import('../locales/zh-TW.json'),
  ar: () => import('../locales/ar.json'),
  he: () => import('../locales/he.json'),
  ja: () => import('../locales/ja.json'),
};

export async function loadStringsAsync(
  language: Locale = 'en' as const
): Promise<Translations> {
  try {
    const importLanguageFn = importMap[language] ?? importMap.en;

    const module = await importLanguageFn();
    return module.default;
  } catch (error) {
    console.error(`Failed to load translations for locale: ${language}`, error);
    // Fallback to English
    try {
      const fallbackModule = await importMap.en();
      return fallbackModule.default;
    } catch (fallbackError) {
      console.error('Failed to load fallback translations', fallbackError);
      return {} as Translations;
    }
  }
}

interface GetTranslationParams {
  translations: Translations;
  key: string;
}

function getTranslation({ translations, key }: GetTranslationParams) {
  if (!translations) {
    console.warn('[l10n] Translations not loaded');
    return key;
  }

  if (!translations[key]) {
    console.error(
      `[l10n] Translation not found for key: "${key}". The original Key value "${key}" will be returned instead.`
    );
    return key;
  }

  return translations[key].message;
}

export function translate(key: string, convertToUpper?: boolean) {
  const translatedString = getTranslation({ translations, key });

  if (convertToUpper) {
    return translatedString.toUpperCase();
  }

  return translatedString;
}
